import { configureStore, Store } from '@reduxjs/toolkit';
import authApi from './rtkQuery/auth';
import { setupListeners } from '@reduxjs/toolkit/query';
import userAccessControlApi from './rtkQuery/userAccessControl';
import utilityApi from './rtkQuery/utility';
import requisitionApi from './rtkQuery/requisition';
import pricingApi from './rtkQuery/pricing';
import settingsApi from './rtkQuery/settings';
import masterApi from './rtkQuery/master';
import processApi from './rtkQuery/process';
import reportsApi from './rtkQuery/reports';
import contractDocumentDraftApi from './rtkQuery/contractManagement';
import tiptapTokenSlice from './slice/contractManagement';
import travelRequestApi from './rtkQuery/travelReq';
import travelRequestSlice from './slices/travelRequestSlice';
import commonCopySlice from './slices/commonCopySlice';
import sapConfigApi from './rtkQuery/sapConfig';
import systemConfigApi from './rtkQuery/systemConfig';
import emailConfigApi from './rtkQuery/emailConfig';
import whatsappConfigApi from './rtkQuery/whatsappConfig';
import tableConfigApi from './rtkQuery/tableConfig';
import contractTableConfigApi from './rtkQuery/contractTableConfig';
import activityTrackingApi from './rtkQuery/activityTracking';
import activityTableConfig from './rtkQuery/activityTableConfig';
import { chatApi } from './rtkQuery/chat';
import paymentReminderApi from './rtkQuery/paymentReminder';
import salesOrderApi from './rtkQuery/salesOrder';
import salesOrderTableConfigApi from './rtkQuery/salesOrderTableConfig';
import salesOrderLineItemTableConfigApi from './rtkQuery/salesOrderLineItemTableConfig';
import masterTableConfigApi from './rtkQuery/masterTableConfig';
import masterListApi from './rtkQuery/masterList';
import viewReportApi from './rtkQuery/viewReport';
import salesOrderDashboardApi from './rtkQuery/salesOrderDashboard';
import { notificationApi } from './rtkQuery/notification';
import advancedPaymentApi from './rtkQuery/advancedPayment';
import advancedPaymentTableConfigApi from './rtkQuery/advancedPaymentTableConfig';
import salesTrackingReportingApi from './rtkQuery/salesTrackingReporting';
import warehouseApi from './rtkQuery/warehouse';
import warehouseTableConfigApi from './rtkQuery/warehouseTableConfig';
import analyticalToolApi from './rtkQuery/analyticalTool';
import { pnlReportApi } from './rtkQuery/pnlReport';
import pnlReportTableConfigApi from './rtkQuery/pnlReportTableConfig';
import purchaseOrderApi from './rtkQuery/purchaseOrder';
import { aiAssistantApi } from './rtkQuery/aiAssistant';

const store: Store = configureStore({
  reducer: {
    [authApi.reducerPath]: authApi.reducer,
    [userAccessControlApi.reducerPath]: userAccessControlApi.reducer,
    [utilityApi.reducerPath]: utilityApi.reducer,
    [requisitionApi.reducerPath]: requisitionApi.reducer,
    [pricingApi.reducerPath]: pricingApi.reducer,
    [settingsApi.reducerPath]: settingsApi.reducer,
    [masterApi.reducerPath]: masterApi.reducer,
    [processApi.reducerPath]: processApi.reducer,
    [reportsApi.reducerPath]: reportsApi.reducer,
    [contractDocumentDraftApi.reducerPath]: contractDocumentDraftApi.reducer,
    [sapConfigApi.reducerPath]: sapConfigApi.reducer,
    [systemConfigApi.reducerPath]: systemConfigApi.reducer,
    [emailConfigApi.reducerPath]: emailConfigApi.reducer,
    [whatsappConfigApi.reducerPath]: whatsappConfigApi.reducer,
    [activityTrackingApi.reducerPath]: activityTrackingApi.reducer,
    [activityTableConfig.reducerPath]: activityTableConfig.reducer,
    [tableConfigApi.reducerPath]: tableConfigApi.reducer,
    [contractTableConfigApi.reducerPath]: contractTableConfigApi.reducer,
    [tiptapTokenSlice.reducerPath]: tiptapTokenSlice.reducer,
    [travelRequestApi.reducerPath]: travelRequestApi.reducer,
    [travelRequestSlice.reducerPath]: travelRequestSlice.reducer,
    [commonCopySlice.reducerPath]: commonCopySlice.reducer,
    [chatApi.reducerPath]: chatApi.reducer,
    [paymentReminderApi.reducerPath]: paymentReminderApi.reducer,
    [salesOrderApi.reducerPath]: salesOrderApi.reducer,
    [salesOrderTableConfigApi.reducerPath]: salesOrderTableConfigApi.reducer,
    [salesOrderLineItemTableConfigApi.reducerPath]:
      salesOrderLineItemTableConfigApi.reducer,
    [masterTableConfigApi.reducerPath]: masterTableConfigApi.reducer,
    [masterListApi.reducerPath]: masterListApi.reducer,
    [viewReportApi.reducerPath]: viewReportApi.reducer,
    [salesOrderDashboardApi.reducerPath]: salesOrderDashboardApi.reducer,
    [notificationApi.reducerPath]: notificationApi.reducer,
    [advancedPaymentApi.reducerPath]: advancedPaymentApi.reducer,
    [advancedPaymentTableConfigApi.reducerPath]:
      advancedPaymentTableConfigApi.reducer,
    [salesTrackingReportingApi.reducerPath]: salesTrackingReportingApi.reducer,
    [warehouseApi.reducerPath]: warehouseApi.reducer,
    [warehouseTableConfigApi.reducerPath]: warehouseTableConfigApi.reducer,
    [analyticalToolApi.reducerPath]: analyticalToolApi.reducer,
    [pnlReportApi.reducerPath]: pnlReportApi.reducer,
    [pnlReportTableConfigApi.reducerPath]: pnlReportTableConfigApi.reducer,
    [purchaseOrderApi.reducerPath]: purchaseOrderApi.reducer,
    [aiAssistantApi.reducerPath]: aiAssistantApi.reducer,
  },
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat([
      authApi.middleware,
      userAccessControlApi.middleware,
      utilityApi.middleware,
      requisitionApi.middleware,
      pricingApi.middleware,
      settingsApi.middleware,
      masterApi.middleware,
      processApi.middleware,
      reportsApi.middleware,
      contractDocumentDraftApi.middleware,
      sapConfigApi.middleware,
      systemConfigApi.middleware,
      emailConfigApi.middleware,
      whatsappConfigApi.middleware,
      tableConfigApi.middleware,
      contractTableConfigApi.middleware,
      travelRequestApi.middleware,
      activityTrackingApi.middleware,
      activityTableConfig.middleware,
      chatApi.middleware,
      paymentReminderApi.middleware,
      salesOrderApi.middleware,
      salesOrderTableConfigApi.middleware,
      salesOrderLineItemTableConfigApi.middleware,
      masterTableConfigApi.middleware,
      masterListApi.middleware,
      viewReportApi.middleware,
      salesOrderDashboardApi.middleware,
      notificationApi.middleware,
      advancedPaymentApi.middleware,
      advancedPaymentTableConfigApi.middleware,
      salesTrackingReportingApi.middleware,
      warehouseApi.middleware,
      warehouseTableConfigApi.middleware,
      analyticalToolApi.middleware,
      pnlReportApi.middleware,
      pnlReportTableConfigApi.middleware,
      purchaseOrderApi.middleware,
      aiAssistantApi.middleware,
    ]),
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;

setupListeners(store.dispatch);

// rtk queries

export {
  useGetAuthConfigurationQuery,
  useGetPendingSsoSessionsMutation,
  useCompletePendingSsoLoginMutation,
  useReplacePendingSsoSessionMutation,
  useLoginMutation,
  useLogoutMutation,
  useCheckSessionQuery,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useVerifyingUserMutation,
  useChangePasswordMutation,
  useGetUserDetailsQuery,
  useUploadProfilePictureMutation,
  useGetProfilePictureQuery,
  useLazyGetProfilePictureQuery,
  useSendOtpMutation,
  useVerifyOtpMutation,
  useGetActiveSessionsQuery,
  useLogoutSessionMutation,
  usePublicGetSessionsMutation,
  usePublicLogoutSessionMutation,
} from './rtkQuery/auth';

export {
  useGetUacUsersQuery,
  useGetSystemPermissionsQuery,
  useGetSystemRolesQuery,
  useLazyGetUsersToRolesQuery,
  useLazyGetUsersToPermissionsQuery,
  useGetUsersToRolesQuery,
  useCreateSystemPermissionMutation,
  useCreateSystemRolesMutation,
  useCreateSystemUserToRolesMutation,
  useCreateSystemUserToPermissionsMutation,
  useCreateUacUserMutation,
  useCreateBulkUacUsersMutation,
  useSendBulkOnboardingMailMutation,
  useUpdateSystemPermissionMutation,
  useUpdateSystemRoleMutation,
  useDeleteSystemPermissionMutation,
  useDeleteSystemRoleMutation,
  useDeleteUacUserMutation,
  useChangeUacUserPasswordMutation,
  useLazyCheckEmailAvailabilityQuery,
  useUpdateUacUserMutation,
  useGetAllUsersQuery,
  useLazyGetAllUsersQuery,
  useGetAllUsersSalesOrderQuery,
  useLazyGetAllCompanyGroupedUsersQuery,
  useGetChangelogUacUserQuery,
} from './rtkQuery/userAccessControl';

export {
  useLazyGetSampleCsvForTableQuery,
  useLazyGetMasterTableSampleCsvQuery,
  useGetWorkingTablesQuery,
  useGetMasterTableNamesQuery,
  useBulkUploadCsvMutation,
  useMasterTableUploadCsvMutation,
} from './rtkQuery/utility';

export {
  useGetRequisitionMaterialQuery,
  useGetRequisitionBillToQuery,
  useGetRequisitionShipToQuery,
  useGetRequisitionCustomerQuery,
  useCreateRequisitionMutation,
  useGetRequisitionMaterialUomQuery,
  useUpdateRequisitionByIdMutation,
  useLazyGetRequisitionMaterialQuery,
  useLazyGetRequisitionBillToQuery,
  useLazyGetRequisitionShipToQuery,
  useLazyGetRequisitionCustomerQuery,
  useGetRequisitionQuery,
  useLazyGetRequisitionQuery,
  useGetRequisitionByIdQuery,
  useDeleteRequisitionByIdMutation,
  useGetRequisitionLogsByIdQuery,
  useLazyGetRequisitionLogsByIdQuery,
  useGetSubmittedRequisitionForApprovalsQuery,
  useGetApprovalLogsQuery,
  useGetRequisitionReportDefaultConfigQuery,
  useGetRequisitionReportQuery,
  useCreateRequisitionReportConfigMutation,
  useGetAllRequisitionReportConfigVariantsQuery,
  useDeleteRequisitionReportConfigMutation,
  useUpdateRequisitionReportGlobalVariantConfigMutation,
  useUpdateRequisitionReportConfigMutation,
  useUpdateRequisitionReportDefaultVariantConfigMutation,
  usePushToErpByIdMutation,
  useLazyDownloadDataInExcelQuery,
} from './rtkQuery/requisition';

export {
  useCreatePricingRequestMutation,
  useGetPricingConditionsQuery,
  useGetSubmittedPricingConditionsQuery,
  useGetPricingConditionByIdQuery,
  useUpdatePricingRequestMutation,
  useDeletePricingRequestMutation,
  useGetPricingConditionReportQuery,
  useLazyPricingdownloadDataInExcelQuery,
} from './rtkQuery/pricing';

export {
  useGetCustomerStatementQuery,
  useGetActiveReportConfigQuery,
  useGetAllReportConfigQuery,
  useCreateReportConfigMutation,
  useUpdateReportConfigMutation,
  useDeleteReportConfigMutation,
  useUpdateReportGlobalVariantConfigMutation,
  useUpdateReportDefaultVariantConfigMutation,
  useLazyGetCustomerStatementReportToExcelQuery,
} from './rtkQuery/reports';

export {
  useGetSystemModulesQuery,
  useGetApprovalConfigFromModuleQuery,
  useLazyGetApprovalConfigFromModuleQuery,
  useCreateApprovalConfigForModuleMutation,
  useGetAllActionQuery,
  useGetAllStatusQuery,
} from './rtkQuery/settings';

export {
  useGetSalesOrganisationQuery,
  useGetDistributionChannelQuery,
  useGetDistributionChannelByIdQuery,
  useLazyGetDistributionChannelByIdQuery,
  useGetCustomerGroupQuery,
  useGetCustomerGroup1Query,
  useGetPriceGroupQuery,
  useGetPriceCategoryQuery,
  useGetStateQuery,
  useGetDivisionQuery,
  useGetCurrencyQuery,
  useGetMaterialGroupQuery,
  useGetMaterialQuery,
  useGetCustomerQuery,
  useGetCompanyBandQuery,
  useLazyGetCompanyBandLevelQuery,
  useGetEmploymentTypeQuery,
  useGetAllDepartmentsQuery,
  useGetAllGradesQuery,
  useGetAllCustomersQuery,
  useGetAllMonthsQuery,
  useGetClientSubCompaniesQuery,
  useGetCompanyGroupQuery,
  useGetModulesQuery,

  // Data Masking hooks
  useGetTablesQuery,
  useGetTableColumnsQuery,
  useGetMaskingDataQuery,
  useLazyGetMaskingDataQuery,
  useGetMaskingDataByIdQuery,
  useLazyGetMaskingDataByIdQuery,
  useCreateMaskingDataMutation,
  useUpdateMaskingDataMutation,
  // Sales Order Master Data hooks
  useGetSalesOrderTypesQuery,
  useGetPlantsQuery,
  useGetTaxCodesQuery,
  useGetBillTosQuery,
  useGetBillTosByCustomerQuery,
  useGetShipTosQuery,
  useGetShipTosByCustomerQuery,
  useGetShipToDropdownQuery,
  useLazyGetShipToDropdownQuery,
  useCreateShipToForCustomerMutation,
  useVerifyCustomerNameMutation,
  useVerifyMaterialLongDescriptionMutation,
  useGetUOMsQuery,
  useGetIncotermsQuery,
  useGetPaymentTermsQuery,
  useGetProductTypesQuery,
  useGetProductGroupsQuery,
  useGetGSTCodesQuery,
  useGetMaterialCategoryQuery,
  // Material Groups 1/2/3
  useGetMaterialGroups1Query,
  useGetMaterialGroups2Query,
  useGetMaterialGroups3Query,
  useGetMaterialGroups4Query,
  // Create Material basic
  useCreateMaterialBasicMutation,
  // Get Material Sales Defaults
  useLazyGetMaterialSalesDefaultsQuery,
  useGetRegionsQuery,
  useGetRegion1DropdownQuery,
  useGetRegion2DropdownQuery,
  useGetRegion3DropdownQuery,
  useGetPackingTypesDropdownQuery,
  useGetHuTypeDropdownQuery,
  // PO Master Dropdowns
  useGetPurchaseOrderTypesQuery,
  useGetPurchaseGroupsQuery,
  useGetProcurementCategoriesQuery,
  useGetTdsTaxCodesQuery,
  useGetAssetsDropdownQuery,
  useGetProjectsDropdownQuery,
  // Inline Creation Mutations
  useCreateVendorInlineMutation,
  useCreateCostCenterInlineMutation,
  useCreateAssetInlineMutation,
  useCreateProjectInlineMutation,
  useGetHSNCodesQuery
} from './rtkQuery/master';

export {
  useGetProcessApprovalActionsQuery,
  useLazyGetProcessApprovalActionsQuery,
  useUpdateProcessApprovalMutation,
  useGetProcessApprovalLogsQuery,
  useProcessLogsByIdQuery,
} from './rtkQuery/process';

export {
  useGetCountriesQuery,
  useGetCitiesQuery,
  useGetClaimCategoriesQuery,
  useGetClaimSubcategoriesQuery,
  useGetCostCentersQuery,
  useGetCurrenciesQuery,
  useGetJourneyModesQuery,
  useGetAccommodationTypesQuery,
  useGetExpenseTypesQuery,
  useGetTravelExpenseTypesByClaimQuery,
  useGetPaymentMethodsQuery,
  useGetTransportModesQuery,
  useCreateTravelRequestMutation,
  useLazyDownloadConveyanceBulkTemplateQuery,
  useParseConveyanceBulkUploadMutation,
  useUpdateTravelRequestMutation,
  useDeleteTravelRequestMutation,
  useGetTravelRequestsQuery,
  useGetTravelPortfolioCountsQuery,
  useGetTravelRequestByIdQuery,
  useGetSubmittedTravelRequestsQuery,
  useGetTravelRequestReportQuery,
  useLazyDownloadTravelRequestReportQuery,
  useGetStatesQuery,
  useGetGstCodesQuery,
  useGetGlAccountsQuery,
  useGetVendorsQuery,
  useGetHandsetLimitMappingQuery,
  useCreateHandsetLimitMappingMutation,
  useDeleteHandsetLimitMappingMutation,
  useGetClaimCategoriesForReqQuery,
  useLazyGetClaimCategoriesForMappingQuery,
  useLazyGetClaimSubCategoriesForMappingQuery,
  useLazyGetClaimSubcategoriesForReqQuery,
  useLazyGetClaimSubcategoriesQuery,
  useGetResolveClaimTypeQuery,
  useLazyGetResolveClaimTypeQuery,
  useCreateFormFieldMappingMutation,
  useGetFormFieldMappingQuery,
  useGetFormFieldMappingByIdQuery,
  useDeleteFormFieldMappingMutation,
  useUpdateFormFieldMappingMutation,
  useGetCustomersQuery,
  useGetDsaCodeQuery,
  useGetLanNoQuery,
  useLazyGetFormFieldMappingQuery,
  useGetTravelApprovalMappingQuery,
  useLazyGetTravelApprovalHierarchyQuery,
  useCreateTravelApprovalMappingMutation,
  useUpdateTravelApprovalMappingMutation,
  useGetTravelApprovalMappingByIdQuery,
  useDeleteTravelApprovalMappingMutation,
  useUploadAttachmentsMutation,
  useGetTravelAttachmentQuery,
  useLazyGetTravelAttachmentQuery,
  useLazyGetTravelRequestAttachmentsQuery,
  useLazyGetTravelRequestAttachmentFileQuery,
  useLazyGetTravelAttachmentMappingsQuery,
  useGetAttachmentGalleryQuery,
  useUploadAttachmentGalleryMutation,
  useLazyGetAttachmentGalleryFileQuery,
  useParseDocumentMutation,
  useCreateOcrDraftMutation,
  useApproveTravelRequestMutation,
  useSubmitTravelRequestMutation,
  useGetTravelApprovalsQuery,
  useLazyGetTravelModeOfTransportLimitQuery,
  usePushClaimToErpByIdMutation,
  useDownloadClaimJSONByIdMutation,
  useLazyGetMobileApplicationLinkQuery,
  useUpdateAttachmentsMutation,
  useLazyGetExpenseTypeLimitsQuery,
  useApproveNewRequestFromEmailMutation,
  useGetConeyanceEntryQuery,
  useGetSummaryQuery,
  useGetMonthlyClaimsQuery,
  useGetApprovalFunnelQuery,
  useGetAverageApprovalTimeQuery,
  useGetPendingApprovalRequestsQuery,
  useGetTopVisitedCitiesQuery,
  useGetTopExpenseTypesQuery,
  useCreateCityMappingMutation,
  useCreateStateMappingMutation,
  useCreateCountryMappingMutation,
  useCreateCountryCityMappingMutation,
  useCreateBulkCountriesMutation,
  useCreateBulkStatesMutation,
  useCreateBulkCitiesMutation,
  useGetPreApprovedTourRequestsQuery,
  useLazyGetFormFieldMappingForExpenseQuery,
  useSendTravelRequestForClaimMutation,
  useLazyGetTravelLimitExpenseTypeQuery,
  useDownloadTravelAttachmentsZipQuery,
  useLazyPrintTravelRequestQuery,
  useGetFormFieldMappingListQuery,
  useCheckTravelCreateAccessQuery,
  useGetAttachmentRecoveryQuery,
  useLazyGetAttachmentRecoveryQuery,
  useGetTravelHomeDashboardQuery,
} from './rtkQuery/travelReq';

export {
  useGetTableConfigListQuery,
  useGetTableConfigQuery,
  useGetCurrentTableConfigQuery,
  useCreateTableConfigMutation,
  useUpdateTableConfigMutation,
  useApplyTableConfigMutation,
  useDeleteTableConfigMutation,
  TableConfigPage,
} from './rtkQuery/tableConfig';

export {
  useGetContractTableConfigListQuery,
  useGetContractTableConfigQuery,
  useGetCurrentContractTableConfigQuery,
  useCreateContractTableConfigMutation,
  useUpdateContractTableConfigMutation,
  useDeleteContractTableConfigMutation,
  useApplyContractTableConfigMutation,
  ContractTableConfigPage,
} from './rtkQuery/contractTableConfig';

export {
  useGetContractTypesDropdownQuery,
  useGetInvoiceFrequencyOptionsQuery,
  useCreateContractDraftMutation,
  useCreateContractWithReferenceMutation,
  useCreateRenewalContractMutation,
  useGetContractRequestSubcontractsQuery,
  useUpdateContractDraftMutation,
  useGetContractsQuery,
  useGetContractDraftByIdQuery,
  useUploadContractAttachmentMutation,
  useGetContractAttachmentsQuery,
  useDeleteContractMutation,
  useGetOperationalUnitsQuery,
  useGetContractApprovalMappingQuery,
  useLazyGetContractApprovalHierarchyQuery,
  useCreateContractApprovalMappingMutation,
  useUpdateContractApprovalMappingMutation,
  useGetContractApprovalMappingByIdQuery,
  useDeleteContractApprovalMappingMutation,
  useLazyDownloadContractReportQuery,
  useApproveContractFromEmailMutation,
  // Contract Analytics
  useGetContractSummaryQuery,
  useGetContractApprovalFunnelQuery,
  useGetContractPendingApprovalRequestsQuery,
} from './rtkQuery/contractManagement';

export {
  useUploadPaymentReminderFileMutation,
  useGetPaymentReminderFilesQuery,
  useGetPaymentReminderAgingSummaryQuery,
  useGetCustomerInvoicesQuery,
  useSendPaymentReminderEmailMutation,
  useGetMailLogsQuery,
  useCreateCustomerRemarkMutation,
  useGetCustomerRemarksQuery,
  useGetAllInvoicesQuery,
  useGetCustomersForDropdownQuery,
  useBulkSendPaymentReminderEmailMutation,
  useUpdateCustomerRemarkMutation,
  useDeleteCustomerRemarkMutation,
  useLazyDownloadTemplateQuery,
  useLazyGetFilePreviewQuery,
  useLazyExportAgingSummaryQuery,
  useUpdatePaymentReminderFileMutation,
  useDeletePaymentReminderFileMutation,
  useGetBucketInvoiceDetailsQuery,
  useCreateBankDetailsMutation,
  useGetBankDetailsQuery,
  useGetBankDetailsByIdQuery,
  useUpdateBankDetailsMutation,
  useDeleteBankDetailsMutation,
  useGetCurrencyValuesQuery,
  useCreateCurrencyValueMutation,
  useDeleteCurrencyValueMutation,
} from './rtkQuery/paymentReminder';

export {
  useGetSalesOrderTypesDropdownQuery,
  useGetSalesOrdersQuery,
  useCreateSalesOrderDraftMutation,
  useGetSalesOrderByIdQuery,
  useGetSalesOrderApprovalMappingQuery,
  useGetSalesOrderApprovalMappingByIdQuery,
  useCreateSalesOrderApprovalMappingMutation,
  useUpdateSalesOrderApprovalMappingMutation,
  useDeleteSalesOrderApprovalMappingMutation,
  useLazyGetSalesOrderApprovalHierarchyQuery,
  // Sales Order Form Field Mapping hooks
  useGetSalesOrderFormFieldMappingsQuery,
  useCreateSalesOrderFormFieldMappingMutation,
  useUpdateSalesOrderFormFieldMappingMutation,
  useDeleteSalesOrderFormFieldMappingMutation,
  useGetSalesOrderFormFieldMappingByIdQuery,
  useGetSalesOrderUniversalFieldQuery,
  // Sales Order Universal Field Set hooks
  useCreateSalesOrderUniversalFieldMutation,
  useUpdateSalesOrderUniversalFieldMutation,
  useDeleteSalesOrderUniversalFieldMutation,
  // Sales Order Draft and Approval hooks
  useGetFormFieldMappingsBySalesOrderTypeQuery,
  useUpdateSalesOrderDraftMutation,
  useSendSalesOrderForApprovalMutation,
  useGetSalesOrderDraftByIdQuery,
  // Sales Order Attachments hooks
  useGetSalesOrderAttachmentsQuery,
  useUploadSalesOrderAttachmentMutation,
  useGetSalesOrderAttachmentFileQuery,
  useLazyGetSalesOrderAttachmentFileQuery,
  // Sales Order Material/Line Items hooks
  useSearchMaterialsQuery,
  useLazySearchMaterialsQuery,
  // Sales Order Customer Search hooks
  useSearchCustomersQuery,
  useLazySearchCustomersQuery,
  useGetSalesOrderCustomerDetailsQuery,
  useLazyGetSalesOrderCustomerDetailsQuery,
  useLazyGetCustomerSalesDefaultsWithBillShipQuery,
  useCreateCustomerMutation,
  useAssociateMaterialToSalesOrderMutation,
  useRemoveMaterialFromSalesOrderMutation,
  useCreateMaterialAndAssociateToSalesOrderMutation,
  useUpdateMaterialThroughSalesOrderMutation,
  // Sales Order Recent drafts hook
  useGetRecentSalesOrderDraftsQuery,
  // Sales Order Approvals hooks
  useGetSalesOrderApprovalsQuery,
  useUpdateSalesOrderApprovalMutation,
  useBulkApproveSalesOrdersMutation,
  useGetSalesOrderApprovalLogsByIdQuery,
  useApproveSalesOrderFromEmailMutation,
  // Sales Order Reports hooks
  useGetSalesOrderReportsQuery,
  useCreateSalesOrderAiReportShareUrlMutation,
  useGetSalesOrderAiReportDashboardQuery,
  useGetSalesOrderLineItemReportsQuery,
  useLazyGetSalesOrderLineItemReportsQuery,
  useLazyExportSalesOrderLineItemsQuery,
  useGetSalesOrderReportChangeLogsQuery,
  useLazyExportSalesOrderReportsQuery,
  // Sales Order Analytics hooks
  useGetSalesOrderSummaryQuery,
  useGetSalesOrderApprovalFunnelQuery,
  useGetSalesOrderPendingApprovalRequestsQuery,
  useGetSalesOrderRevenueMetricsQuery,
  useGetSalesOrderTopCustomersQuery,
  useGetSalesOrderTopMaterialsQuery,
  useGetSalesOrderSalesByOrganizationQuery,
  useGetSalesOrderSalesByDistributionChannelQuery,
  useGetSalesOrderSalesByPlantQuery,
  useGetSalesOrderTypeDistributionQuery,
  useGetSalesOrderCustomerGroupDistributionQuery,
  useGetSalesOrderSalesByCountryQuery,
  useGetSalesOrderMonthlyOrderTrendsQuery,
  useGetSalesOrderDeliveryDateAnalyticsQuery,
  useGetSalesOrderCurrencyDistributionQuery,
  // Sales Order Logs hooks
  useGetSalesOrderChangeLogsByIdQuery,
  useGetSalesOrderActivityLogsByIdQuery,
  // Sales Order Actions hooks
  useDeleteSalesOrderMutation,
  useLazyPrintSalesOrderQuery,
  useDownloadSalesOrderJSONMutation,
  useLazyDownloadSalesOrderAttachmentJSONQuery,
  useLazyDownloadSalesOrderAttachmentsZipQuery,
  useCreateSalesOrderWithReferenceMutation,
  useCalculateSalesOrderValuesMutation,
  useGetSalesOrderFilterOptionsQuery,
  useGetSalesOrderSortOptionsQuery,
  usePushSalesOrderToErpMutation,
  useLazyGetSalesOrderForErpPushOrExportQuery,
  // Sales Order Exact Form Field Mapping
  useGetSalesOrderExactFormFieldMappingQuery,
} from './rtkQuery/salesOrder';

export {
  useGetSalesOrderTableConfigListQuery,
  useGetActiveSalesOrderTableConfigQuery,
  useGetSalesOrderTableConfigQuery,
  useLazyGetSalesOrderTableConfigQuery,
  useCreateSalesOrderTableConfigMutation,
  useUpdateSalesOrderTableConfigMutation,
  useDeleteSalesOrderTableConfigMutation,
  useApplySalesOrderTableConfigMutation,
  SalesOrderTableConfigPage,
} from './rtkQuery/salesOrderTableConfig';

export {
  useGetSalesOrderLineItemTableConfigListQuery,
  useGetActiveSalesOrderLineItemTableConfigQuery,
  useGetSalesOrderLineItemTableConfigQuery,
  useLazyGetSalesOrderLineItemTableConfigQuery,
  useCreateSalesOrderLineItemTableConfigMutation,
  useUpdateSalesOrderLineItemTableConfigMutation,
  useDeleteSalesOrderLineItemTableConfigMutation,
  useApplySalesOrderLineItemTableConfigMutation,
} from './rtkQuery/salesOrderLineItemTableConfig';
export {
  useGetAdvancedPaymentTableConfigListQuery,
  useGetActiveAdvancedPaymentTableConfigQuery,
  useGetAdvancedPaymentTableConfigQuery,
  useCreateAdvancedPaymentTableConfigMutation,
  useUpdateAdvancedPaymentTableConfigMutation,
  useDeleteAdvancedPaymentTableConfigMutation,
  useApplyAdvancedPaymentTableConfigMutation,
  AdvancedPaymentTableConfigPage,
} from './rtkQuery/advancedPaymentTableConfig';
export {
  useLazyGetMyNotificationsQuery,
  useMarkNotificationReadMutation,
  useSendPushNotificationMutation,
} from './rtkQuery/notification';

export {
  useGetSalesTrackingReportQuery,
  useLazyExportSalesTrackingReportQuery,
  useGetTrackingReportTableConfigListQuery,
  useGetActiveTrackingReportTableConfigQuery,
  useGetTrackingReportTableConfigQuery,
  useLazyGetTrackingReportTableConfigQuery,
  useCreateTrackingReportTableConfigMutation,
  useUpdateTrackingReportTableConfigMutation,
  useDeleteTrackingReportTableConfigMutation,
  useApplyTrackingReportTableConfigMutation,
} from './rtkQuery/salesTrackingReporting';

export {
  useGetWarehouseHierarchyQuery,
  useCreateWarehouseNodeMutation,
  useUploadWarehouseNodesCsvMutation,
  useLazyDownloadWarehouseStructureSampleQuery,
  useGetWarehouseMasterQuery,
  useGetWarehouseDropdownQuery,
  useGetWarehouseListOnUserQuery,
  useDeleteWarehouseNodeMutation,
  useUpdateWarehouseNodeMutation,
  useGetWarehouseBinDetailsQuery,
  useGetAccessTypeFieldQuery,
  useGetInventoryCategoryFieldQuery,
  useCreateInventoryCategoryFieldMutation,
  useDeleteInventoryCategoryFieldMutation,
  useUpdateInventoryCategoryFieldMutation,
  useGetVehicleTypeFieldQuery,
  useCreateVehicleTypeFieldMutation,
  useDeleteVehicleTypeFieldMutation,
  useUpdateVehicleTypeFieldMutation,
  useGetWarehouseUniversalFieldsQuery,
  useCreateWarehouseUniversalFieldMutation,
  useUpdateWarehouseUniversalFieldMutation,
  useDeleteWarehouseUniversalFieldsMutation,
  useGetTransactionTypeFieldQuery,
  useCreateTransactionTypeFieldMutation,
  useDeleteTransactionTypeFieldMutation,
  useUpdateTransactionTypeFieldMutation,
  useGetTransactionSubTypeFieldQuery,
  useCreateTransactionSubTypeFieldMutation,
  useDeleteTransactionSubTypeFieldMutation,
  useUpdateTransactionSubTypeFieldMutation,
  useGetTransactionCompanyMappingFieldQuery,
  useCreateTransactionCompanyMappingFieldMutation,
  useDeleteTransactionCompanyMappingFieldMutation,
  useUpdateTransactionCompanyMappingFieldMutation,
  useGetWarehouseFormFieldMappingsQuery,
  useGetWarehouseFormFieldMappingByIdQuery,
  useCreateWarehouseFormFieldMappingMutation,
  useCreateWarehouseFormFieldMappingWithReferenceMutation,
  useUpdateWarehouseFormFieldMappingMutation,
  useDeleteWarehouseFormFieldMappingsMutation,
  useGetUserNodeMappingQuery,
  useCreateUserNodeMappingMutation,
  useUpdateUserNodeMappingMutation,
  useDeleteUserNodeMappingMutation,
  useGetNumberRangeQuery,
  useCreateNumberRangeMutation,
  useUpdateNumberRangeMutation,
  useDeleteNumberRangeMutation,
  useGetNodeTypeQuery,
  useCreateNodeTypeMutation,
  useUpdateNodeTypeMutation,
  useDeleteNodeTypeMutation,
  useGetAssetQuery,
  useGetAssetByIdQuery,
  useSearchAssetsQuery,
  useLazySearchAssetsQuery,
  useCreateAssetMutation,
  useUpdateAssetMutation,
  useDeleteAssetMutation,
  useGetMaterialTypeDropdownQuery,
  useGetBinCapacityMappingQuery,
  useGetBinCapacityMappingByIdQuery,
  useCreateBinCapacityMappingMutation,
  useUpdateBinCapacityMappingMutation,
  useDeleteBinCapacityMappingMutation,
  useLazyDownloadBinCapacitySampleQuery,
  useUploadBinCapacityMappingMutation,
  useGetZoneGroupMappingQuery,
  useGetZoneGroupMappingByIdQuery,
  useCreateZoneGroupMappingMutation,
  useUpdateZoneGroupMappingMutation,
  useDeleteZoneGroupMappingMutation,
  useGetWarehouseItemMappingQuery,
  useGetWarehouseItemMappingByIdQuery,
  useCreateWarehouseItemMappingMutation,
  useUpdateWarehouseItemMappingMutation,
  useDeleteWarehouseItemMappingMutation,
  useSearchWarehouseBatchNoQuery,
  useLazySearchWarehouseBatchNoQuery,
  useLazyExportWarehouseBatchNoQuery,
} from './rtkQuery/warehouse';

export {
  useGetAnalyticsTablesQuery,
  useGetAnalyticsViewsQuery,
  useGetAnalyticsColumnsQuery,
  useCreateAnalyticsDatasetMutation,
  useGetAnalyticsDatasetsQuery,
  useGetAnalyticsDatasetQuery,
  useUpdateAnalyticsDatasetMutation,
  useDeleteAnalyticsDatasetMutation,
  useIntrospectAnalyticsDatasetMutation,
  usePreviewAnalyticsDatasetMutation,
  useUpdateAnalyticsDatasetFieldsMutation,
  useCreateAnalyticsChartMutation,
  useGetAnalyticsChartsQuery,
  useGetAnalyticsChartQuery,
  useUpdateAnalyticsChartMutation,
  useDeleteAnalyticsChartMutation,
  useGetAnalyticsChartDataMutation,
  useGetAnalyticsChartSqlQuery,
  useLazyGetAnalyticsChartSqlQuery,
  useCreateAnalyticsDashboardMutation,
  useGetAnalyticsDashboardsQuery,
  useGetAnalyticsDashboardQuery,
  useUpdateAnalyticsDashboardMutation,
  useDeleteAnalyticsDashboardMutation,
  useGetAnalyticsDashboardDataMutation,
  useCreateAnalyticsEmbedTokenMutation,
  useGetEmbeddedAnalyticsDashboardQuery,
  useGetEmbeddedAnalyticsChartDataMutation,
} from './rtkQuery/analyticalTool';

export {
  useGetPnlReportsQuery,
  useLazyGetPnlReportsQuery,
  useLazyExportPnlReportsQuery,
} from './rtkQuery/pnlReport';

export {
  useGetAiSessionsQuery,
  useCreateAiSessionMutation,
  useGetAiSessionByIdQuery,
  useUpdateAiSessionTitleMutation,
  useDeleteAiSessionMutation,
  useSendAiChatMessageMutation,
} from './rtkQuery/aiAssistant';

export {
  useGetPnlReportTableConfigListQuery,
  useGetCurrentPnlReportTableConfigQuery,
  useGetPnlReportTableConfigByIdQuery,
  useCreatePnlReportTableConfigMutation,
  useUpdatePnlReportTableConfigMutation,
  useDeletePnlReportTableConfigMutation,
  useApplyPnlReportTableConfigMutation,
} from './rtkQuery/pnlReportTableConfig';

export {
  useGetWarehouseTableConfigListQuery,
  useGetWarehouseTableConfigQuery,
  useGetCurrentWarehouseTableConfigQuery,
  useCreateWarehouseTableConfigMutation,
  useUpdateWarehouseTableConfigMutation,
  useApplyWarehouseTableConfigMutation,
  useDeleteWarehouseTableConfigMutation,
  WarehouseTableConfigPage,
} from './rtkQuery/warehouseTableConfig';


export const { setTokenValue } = tiptapTokenSlice.actions;
export const { setCopyData, clearCopyData } = travelRequestSlice.actions;
export const { setCopyId, clearCopyId } = commonCopySlice.actions;

export {
  useGetMasterTableConfigListQuery,
  useGetActiveMasterTableConfigQuery,
  useGetMasterTableConfigQuery,
  useLazyGetMasterTableConfigQuery,
  useCreateMasterTableConfigMutation,
  useUpdateMasterTableConfigMutation,
  useDeleteMasterTableConfigMutation,
  useApplyMasterTableConfigMutation,
} from './rtkQuery/masterTableConfig';

export {
  useGetCustomerListQuery,
  useLazyGetCustomerListQuery,
  useGetMaterialListQuery,
  useLazyGetMaterialListQuery,
} from './rtkQuery/masterList';

export default store;
